import React, { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "./AccountSettings.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { useAuth } from "../../../hooks/useAuth";
import { updateAdminProfile, updateAdminPassword } from "../../../services/authService";

const placeholderImage = process.env.PUBLIC_URL + "/images/placeholder.png";

function getInitialNameParts(user) {
  if (!user) return { firstName: "", lastName: "" };
  if (user.firstName != null || user.lastName != null) {
    return {
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
    };
  }
  const full =
    user.name || user.fullName || "";
  const parts = full.trim().split(/\s+/);
  if (parts.length >= 2) {
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(" "),
    };
  }
  return { firstName: full, lastName: "" };
}

export default function AccountSettings() {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [profilePictureUrl, setProfilePictureUrl] = useState("");
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);

  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");

  const fileInputRef = useRef(null);
  const passwordModalRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const { firstName: f, lastName: l } = getInitialNameParts(user);
    setFirstName(f);
    setLastName(l);
    setEmail(user.email ?? "");
    setProfilePictureUrl(user.profilePictureUrl ?? user.profileImage ?? user.image ?? "");
    setProfilePictureFile(null);
    setProfilePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [user]);

  const displayImage = profilePreviewUrl || profilePictureUrl || placeholderImage;

  const updateProfileMutation = useMutation({
    mutationFn: (formData) => updateAdminProfile(formData),
    onSuccess: (data) => {
      const updated = data?.data ?? data?.admin ?? data;
      updateUser({
        email: updated?.email ?? email,
        firstName: updated?.firstName ?? firstName,
        lastName: updated?.lastName ?? lastName,
        profilePictureUrl: updated?.profilePictureUrl ?? updated?.profileImage ?? updated?.image ?? profilePictureUrl,
      });
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
      setProfilePreviewUrl(null);
      setProfilePictureFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success(data?.message ?? "Profile updated successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      setIsEditing(false);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to update profile";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: (payload) => updateAdminPassword(payload),
    onSuccess: (data) => {
      setOldPass("");
      setNewPass("");
      setConfirmPass("");
      const modalEl = passwordModalRef.current;
      if (modalEl && window.bootstrap?.Modal) {
        const modal = window.bootstrap.Modal.getOrCreateInstance?.(modalEl) ?? window.bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
      }
      toast.success(data?.message ?? "Password updated successfully", {
        position: "top-right",
        autoClose: 3000,
      });
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to update password";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    },
  });

  const handleUpdatePassword = () => {
    const old = oldPass.trim();
    const newP = newPass.trim();
    const confirm = confirmPass.trim();
    if (!old) {
      toast.error("Please enter your current password", { position: "top-right", autoClose: 3000 });
      return;
    }
    if (!newP) {
      toast.error("Please enter a new password", { position: "top-right", autoClose: 3000 });
      return;
    }
    if (newP !== confirm) {
      toast.error("New password and confirm password do not match", { position: "top-right", autoClose: 3000 });
      return;
    }
    updatePasswordMutation.mutate({
      oldPassword: old,
      newPassword: newP,
      confirmPassword: confirm,
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
      setProfilePreviewUrl(URL.createObjectURL(file));
      setProfilePictureFile(file);
    }
  };

  const handleSave = () => {
    if (!email.trim()) {
      toast.error("Email is required", { position: "top-right", autoClose: 3000 });
      return;
    }
    const formData = new FormData();
    formData.append("email", email.trim());
    formData.append("firstName", firstName.trim());
    formData.append("lastName", lastName.trim());
    if (profilePictureFile && profilePictureFile instanceof File) {
      formData.append("profilePicture", profilePictureFile);
    }
    updateProfileMutation.mutate(formData);
  };

  const handleCancel = () => {
    if (!user) return;
    if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
    setProfilePreviewUrl(null);
    setProfilePictureFile(null);
    const { firstName: f, lastName: l } = getInitialNameParts(user);
    setFirstName(f);
    setLastName(l);
    setEmail(user.email ?? "");
    setProfilePictureUrl(user.profilePictureUrl ?? user.profileImage ?? user.image ?? "");
    setIsEditing(false);
  };

  return (
    <>
      <header className="panel-header items-center flex content-justify-between">
        <div className="left-side">
          <h4 className="title">Account Settings</h4>
        </div>
      </header>

      <div className="account-settings-wrapper">
        <div className="row">
          <div className="col-md-10 col-lg-7 mx-auto">
            <div className="settings-card">
              <h2>User Information</h2>

              <div className="profile-section">
                <p className="label">Profile Picture</p>
                <div className="image-wrapper">
                  <img
                    src={displayImage}
                    alt="Profile"
                    className="profile-img"
                    onError={(e) => {
                      e.target.src = placeholderImage;
                    }}
                  />
                  {isEditing && (
                    <div
                      className="edit-icon"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <img src={process.env.PUBLIC_URL + "/images/pen.svg"} alt="pen" />
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </div>
              </div>

              <div className="input-wrap mb-3">
                <label>First name</label>
                <input
                  type="text"
                  value={firstName}
                  disabled={!isEditing}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                />
              </div>

              <div className="input-wrap mb-3">
                <label>Last name</label>
                <input
                  type="text"
                  value={lastName}
                  disabled={!isEditing}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                />
              </div>

              <div className="input-wrap mb-3">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  disabled={!isEditing}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                />
              </div>

              {!isEditing && (
                <div className="input-wrap mb-3">
                  <label>Password</label>
                  <input type="password" value="********" disabled />
                </div>
              )}

              <div className="button-row">
                {!isEditing ? (
                  <>
                    <button
                      className="close-btn"
                      data-bs-toggle="modal"
                      data-bs-target="#passwordModal"
                    >
                      Update Password
                    </button>
                    <button
                      className="submit-btn"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit Profile
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="close-btn"
                      onClick={handleCancel}
                      disabled={updateProfileMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      className="orange-btn"
                      onClick={handleSave}
                      disabled={updateProfileMutation.isPending}
                    >
                      {updateProfileMutation.isPending ? "Saving..." : "Save"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        ref={passwordModalRef}
        className="modal fade admin-modal"
        id="passwordModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content p-3">
            <h5 className="mb-3">Update Password</h5>
            <div className="input-wrap mb-3">
              <label>Old Password</label>
              <input
                type="password"
                className="form-control"
                value={oldPass}
                onChange={(e) => setOldPass(e.target.value)}
                placeholder="Current password"
              />
            </div>
            <div className="input-wrap mb-3">
              <label>New Password</label>
              <input
                type="password"
                className="form-control"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                placeholder="New password"
              />
            </div>
            <div className="input-wrap mb-3">
              <label>Confirm Password</label>
              <input
                type="password"
                className="form-control"
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
            <div className="button-row">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={updatePasswordMutation.isPending}
              >
                Close
              </button>
              <button
                type="button"
                className="orange-btn"
                onClick={handleUpdatePassword}
                disabled={updatePasswordMutation.isPending}
              >
                {updatePasswordMutation.isPending ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
